/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Link } from 'react-router-dom'
import {
  AppIcon,
  Modal,
  ModalButton
} from '@splunk-iteng/splunk-shared-components'
import {
  ApplicationDataInput,
  useGetAdminAppsQuery,
  useGetAppAttributeQuery,
  useAddAppAttributeMutation
} from '../../graphql/graphql'
import { CardZone } from './EditingZone'

type AppList = {
  appName: string
  appSSOid: string
  published: string
}
type AppAttribute = {
  id: string
  app_name: string
  attribute_order: number
  attribute_is_required: boolean
  attribute_is_published: boolean
  attribute_key: string
  attribute_value: string
  attribute_type: string
}

const PREVIEW_STORAGE_KEY = 'abacPreviewState'
const PREVIEW_STORAGE_KEY_PUBLISHED = 'abacPreviewStatePublished'

export function AttributeManagement() {
  const [application, setApplication] = useState('')
  const [appAttribute, setAppAttribute] = useState<AppAttribute[]>([])
  const [appAttributePublished, setAppAttributePublished] = useState<
    AppAttribute[]
  >([])
  const [appList, setAppList] = useState<AppList[]>([])
  const [modalVisibility, setModalVisibility] = useState(false)
  const [modalVisibilitySave, setModalVisibilitySave] = useState(false)
  const [modalHeader, setModalHeader] = useState('Publish this form?')
  const [modalText, setModalText] = useState(
    'When published, the form will be visible to customers. Are you sure you want to publish?'
  )
  const [modalTextSave, setModalTextSave] = useState('Save was successful')
  const [modalButtonsState, setModalButtonsState] = useState<ModalButton[]>([])
  const [AttributeManagementMutation] = useAddAppAttributeMutation()
  const { error: adminError, data: adminData } = useGetAdminAppsQuery({
    fetchPolicy: 'no-cache',
    variables: {}
  })
  const {
    error: appAttributeError,
    data: appAttributeData,
    refetch: refetchGetAppAttribute
  } = useGetAppAttributeQuery({
    fetchPolicy: 'no-cache',
    variables: { appName: application },
    skip: !application || application === ''
  })

  const closeModal = () => {
    setModalVisibility(false)
    setModalHeader('Publish this form?')
    setModalText(
      'When published, the form will be visible to customers. Are you sure you want to publish?'
    )
  }

  const persistPreviewState = useCallback(
    (
      appNameToSave: string,
      appAttributeToSave: AppAttribute[],
      appAttributePublishedToSave: AppAttribute[]
    ) => {
      if (globalThis.window === undefined) return

      try {
        globalThis.window.localStorage.setItem(
          PREVIEW_STORAGE_KEY,
          JSON.stringify({
            appName: appNameToSave,
            appAttribute: appAttributeToSave
          })
        )
        globalThis.window.localStorage.setItem(
          PREVIEW_STORAGE_KEY_PUBLISHED,
          JSON.stringify({
            appName: appNameToSave,
            appAttribute: appAttributePublishedToSave
          })
        )
      } catch (error) {
        console.error('Unable to persist ABAC preview state', error)
      }
    },
    []
  )

  const handleSubmit = async (publish: boolean = false) => {
    const appData: ApplicationDataInput[] =
      appAttribute.length > 0
        ? appAttribute.map((row) => {
            return {
              app_name: application,
              attribute_order: row.attribute_order,
              attribute_is_required: row.attribute_is_required,
              attribute_is_published: publish,
              attribute_key: row.attribute_key,
              attribute_value: row.attribute_value,
              attribute_type: row.attribute_type
            }
          })
        : [
            {
              app_name: application,
              attribute_order: 0,
              attribute_is_required: true,
              attribute_is_published: false,
              attribute_key: 'delete',
              attribute_value: 'delete',
              attribute_type: 'delete'
            }
          ]

    await AttributeManagementMutation({
      variables: {
        data: appData
      },
      onCompleted: () => {
        setModalText('Successfully published the form.')
        setModalTextSave('Save was successful')
        setModalButtonsState([
          {
            label: 'OK',
            onClick: () => {
              setModalVisibility(false)
            },
            className: 'splunk-btn-common splunk-btn-magenta'
          }
        ])
      },
      onError: () => {
        setModalText('There was an error publishing the form.')
        setModalTextSave('There was an error saving the form.')
        setModalButtonsState([
          {
            label: 'Close',
            onClick: () => {
              setModalVisibility(false)
            },
            className: 'splunk-btn-common splunk-btn-magenta'
          }
        ])
      }
    })
  }

  useEffect(() => {
    if (appAttributeError) alert(appAttributeError.message)
    if (adminError) alert(adminError.message)
  }, [appAttributeError, adminError])

  useEffect(() => {
    if (appAttributeData?.getAppAttribute?.data) {
      const existingApp = appAttributeData?.getAppAttribute?.data
      const filteredApp: AppAttribute[] = existingApp
        .filter((item) => !item?.attribute_is_published)
        .map((item) => ({
          id: uuidv4(),
          app_name: application,
          attribute_order: item?.attribute_order ?? 0,
          attribute_is_required: item?.attribute_is_required ?? false,
          attribute_is_published: item?.attribute_is_published ?? false,
          attribute_key: item?.attribute_key ?? '',
          attribute_value: item?.attribute_value ?? '',
          attribute_type: item?.attribute_type ?? ''
        }))
      setAppAttribute(filteredApp)

      const publishedApp = existingApp
        .filter((item) => item?.attribute_is_published)
        .map((item) => ({
          id: uuidv4(),
          app_name: application,
          attribute_order: item?.attribute_order ?? 0,
          attribute_is_required: item?.attribute_is_required ?? false,
          attribute_is_published: item?.attribute_is_published ?? true,
          attribute_key: item?.attribute_key ?? '',
          attribute_value: item?.attribute_value ?? '',
          attribute_type: item?.attribute_type ?? ''
        }))
      setAppAttributePublished(publishedApp)
    }
  }, [appAttributeData, application])

  useEffect(() => {
    setAppList([])
    if (adminData?.getAdminApps?.data) {
      const existingApp = adminData?.getAdminApps?.data.apps!
      setAppList(existingApp)
      if (!application && existingApp?.length > 0) {
        setApplication(existingApp[0]!.appName!)
      }
    }
  }, [adminData, application])

  useEffect(() => {
    persistPreviewState(application, appAttribute, appAttributePublished)
  }, [application, appAttribute, appAttributePublished, persistPreviewState])

  const preventEnter = async (e: React.FormEvent) => {
    e.preventDefault()
  }

  const dataToPublish = (cardData: any[]) => {
    setAppAttribute(cardData)
  }

  return (
    <form onSubmit={preventEnter}>
      <Modal
        name="ModalName"
        isActive={modalVisibility}
        options={{
          header: modalHeader,
          text: modalText,
          buttons: modalButtonsState,
          root: 'content'
        }}
        onSubmitCallback={async () => {}}
        onCloseCallback={closeModal}
      />
      <Modal
        name="ModalName"
        isActive={modalVisibilitySave}
        options={{
          header: '',
          text: modalTextSave,
          buttons: [
            {
              label: 'OK',
              onClick: () => {
                setModalVisibilitySave(false)
              },
              className: 'splunk-btn-common splunk-btn-magenta'
            }
          ],
          root: 'content'
        }}
        onSubmitCallback={async () => {}}
        onCloseCallback={() => setModalVisibilitySave(false)}
      />
      <div className="attribute-page-h1-container">
        {appList.map((app) => (
          <button
            key={uuidv4()}
            type="button"
            className={`carousel-btn ${
              app.appName === application
                ? 'carousel-btn-active'
                : 'carousel-btn-inactive'
            }`}
            onClick={() => {
              setApplication(app.appName)
              refetchGetAppAttribute()
            }}
            style={{ marginRight: 10 }}
          >
            <AppIcon />
            <span>{app.appName}</span>
          </button>
        ))}
      </div>
      <div className="attribute-page-h2-container">
        <div className="attribute-page-h2-container">
          <span className="attribute-page-h1">{application}</span>
          <br />
          <span>
            Collect essential information from new customers during the signup
            process
          </span>
        </div>
        <div className="attribute-page-h2">
          <Link
            className="btn btn-primary"
            to={{
              pathname: '/attribute-management/abac-preview',
              search: `?AppName=${encodeURIComponent(application)}`
            }}
            onClick={() => {
              persistPreviewState(
                application,
                appAttribute,
                appAttributePublished
              )
            }}
            target="_blank"
          >
            <span>Preview</span>
          </Link>
          <button
            type="button"
            className="attribute-remove-btn"
            onClick={() => {
              setAppAttribute([])
            }}
            style={{ marginRight: 10 }}
          >
            Delete Form
          </button>
          <button
            type="button"
            className="splunk-btn-common splunk-btn-magenta"
            onClick={async () => {
              await handleSubmit(false)
              setModalVisibilitySave(true)
            }}
            style={{ marginRight: 10 }}
          >
            Save
          </button>
          <button
            type="button"
            className="attribute-publish-btn"
            onClick={() => {
              setModalButtonsState([
                {
                  label: 'Cancel',
                  onClick: () => {
                    setModalVisibility(false)
                  },
                  className: 'splunk-btn-common splunk-btn-magenta'
                },
                {
                  label: 'Confirm',
                  onClick: async () => {
                    await handleSubmit(false)
                    await handleSubmit(true)
                  },
                  className: 'splunk-btn-common splunk-btn-magenta-solid'
                }
              ])
              setModalText(
                'When published, the form will be visible to customers. Are you sure you want to publish?'
              )
              setModalVisibility(!modalVisibility)
            }}
          >
            Publish
          </button>
        </div>
      </div>
      <div className="attribute-page-h2-container" style={{ marginTop: 50 }}>
        <span className="attribute-page-h1">
          Customize the questions to collect during the Sign-Up flow
        </span>
        <br />
        <span>
          Create the questions new users will answer during registration. This
          will help you collect the relevant info from users.
        </span>
      </div>
      <div className="drag-drop-zone">
        <CardZone
          appName={application}
          cards={appAttribute}
          onSave={dataToPublish}
        />
      </div>
    </form>
  )
}
